import React, { useEffect, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { ApiConfig } from '../../../../config/api'
import axios from 'axios'
import { useStyles } from '../styles'
import { useApi } from '../../../../hooks/useApi'
import { State } from '../../../../components/_EOS/State'
import { Status } from '../../../../hooks/useFetchState'
import { ApiResponse } from '../../../../typings/ApiResponse'
import { StepProps } from '../../../../components/_WMS/Stepper'
import {
  eInventoryCheckSteps,
  InventoryCheckDetail,
  InventoryCheckPosition,
  SharedStateType
} from '../../types'

interface ShowInventoryCheckPositionsProps extends StepProps<SharedStateType> {}

const getInventoryCheckPositions = (
  inventoryCheckId: number,
  userId: number | undefined
) => {
  const url = new URL(
    `${ApiConfig.InventoryCheckApi}/${inventoryCheckId}/positions?pageSize=5&userId=${userId}&availablePositions=true`
  )

  return axios.get(url.toString())
}

const getInventoryCheckAssignedUserPositions = (
  inventoryCheckId: number,
  userId: number | undefined
) => {
  const url = new URL(
    `${ApiConfig.InventoryCheckApi}/${inventoryCheckId}/positions?pageSize=5&userId=${userId}&takenPositions=true`
  )

  return axios.get(url.toString())
}

const patchInventoryCheckCycle = (
  inventoryCheckId: number,
  positionId: number
) => {
  const url = new URL(
    `${ApiConfig.InventoryCheckApi}/${inventoryCheckId}/position/${positionId}`
  )

  return axios.patch(url.toString())
}

export const ShowInventoryCheckPositions: React.FC<
  ShowInventoryCheckPositionsProps
> = ({
  goToStep,
  next,
  setSharedState,
  sharedState: { workOrderId, user }
}) => {
  const classes = useStyles()

  const { state, callApi } = useApi<ApiResponse<InventoryCheckPosition>>()
  const {
    state: userAssignedPositionsState,
    callApi: userAssignedPositionsCallApi
  } = useApi<ApiResponse<InventoryCheckPosition>>()
  const {
    state: patchInventoryCheckState,
    callApi: patchInventoryCheckCallApi
  } = useApi()

  const [countdown, setCountdown] = useState(3)

  const requestGetInventoryCheckPositions = () => {
    if (workOrderId) {
      callApi(getInventoryCheckPositions(workOrderId, user?.userId)).catch(
        (err) => {
          setSharedState((prevState) => ({ ...prevState, error: err }))
          goToStep(eInventoryCheckSteps.Error)
        }
      )
    }
  }

  const requestGetInventoryCheckUserAssignedPositions = () => {
    if (workOrderId) {
      userAssignedPositionsCallApi(
        getInventoryCheckAssignedUserPositions(workOrderId, user?.userId)
      ).catch((err) => {
        setSharedState((prevState) => ({ ...prevState, error: err }))
        goToStep(eInventoryCheckSteps.Error)
      })
    }
  }

  const requestPatchInventoryCheckCycle = (
    inventoryCheckId: number,
    positionId: number
  ) => {
    patchInventoryCheckCallApi(
      patchInventoryCheckCycle(inventoryCheckId, positionId)
    )
      .then((res: InventoryCheckDetail) =>
        setSharedState((prevState) => ({
          ...prevState,
          inventoryCheckDetails: res,
          inventoryCheckDetailId: res.inventoryCheckDetailId
        }))
      )
      .then(next)
      .catch((err) => {
        setSharedState((prevState) => ({ ...prevState, error: err }))
        goToStep(eInventoryCheckSteps.Error)
      })
  }

  useEffect(() => {
    requestGetInventoryCheckUserAssignedPositions()
  }, [])

  useEffect(() => {
    if (
      userAssignedPositionsState.data !== undefined &&
      userAssignedPositionsState.data?.items.length === 0
    ) {
      requestGetInventoryCheckPositions()
    }
  }, [userAssignedPositionsState.data])

  useEffect(() => {
    if (state.data?.items.length === 0 && state.status === Status.Success) {
      setCountdown(3)
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval)
            return 0
          }
          return prev - 1
        })
      }, 1000)
      const timer = setTimeout(() => {
        setSharedState({
          workOrderId: undefined,
          positionCode: undefined,
          error: '',
          user
        })
        goToStep(eInventoryCheckSteps.SearchInventoryCheck)
      }, 3000)
      return () => {
        clearTimeout(timer)
        clearInterval(countdownInterval)
      }
    }
    return undefined
  }, [state.data, state.status])

  const handleSelectPosition = async (position: InventoryCheckPosition) => {
    setSharedState((prevState) => ({
      ...prevState,
      positionCode: position.positionName,
      positionId: position.positionId
    }))
    await requestPatchInventoryCheckCycle(workOrderId!, position.positionId)
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant='body1'>Operador {user?.userAlias}</Typography>
        <Typography variant='body1'>OT {workOrderId}</Typography>
      </Box>
      <Box className={classes.content}>
        {patchInventoryCheckState.status !== Status.Idle && (
          <State
            state={patchInventoryCheckState}
            loadingMessage='Asignando posición'
          ></State>
        )}
        {(state.data?.items.length || 0) === 0 &&
          state.status === Status.Success && (
            <Box className={classes.contentContainer}>
              <Typography variant='body2'>
                No existen posiciones disponibles para procesar
              </Typography>

              <Typography variant='body2' style={{ marginTop: '16px' }}>
                Volviendo a la pantalla de inicio en {countdown}...
              </Typography>
            </Box>
          )}
        {(state.data?.items.length || 0) > 0 &&
          state.status !== Status.Success && (
            <Box className={classes.contentContainer}>
              <Typography variant='body2'>
                Las siguientes posiciones se encuentran disponibles
              </Typography>
            </Box>
          )}
        {state.status !== Status.Idle && (
          <Box className={classes.listButtonContainer}>
            <State
              state={state}
              loadingMessage='Obteniendo posiciones disponibles...'
            >
              {state.data?.items.map((position) => (
                <Button
                  fullWidth
                  variant='outlined'
                  color='primary'
                  onClick={() => handleSelectPosition(position)}
                  disabled={patchInventoryCheckState.status === Status.Loading}
                >
                  {position.positionName}
                </Button>
              ))}
            </State>
          </Box>
        )}
        {(userAssignedPositionsState.data?.items.length || 0) > 0 && (
          <Box className={classes.contentContainer}>
            <Typography variant='body2'>
              Tiene la siguiente posición asignada
            </Typography>
          </Box>
        )}
        {userAssignedPositionsState.status !== Status.Idle && (
          <State
            state={userAssignedPositionsState}
            loadingMessage='Identificando posiciones asignadas...'
          >
            {userAssignedPositionsState.data?.items.map((position) => (
              <Button
                fullWidth
                variant='outlined'
                color='primary'
                onClick={() => handleSelectPosition(position)}
                disabled={patchInventoryCheckState.status === Status.Loading}
              >
                {position.positionName}
              </Button>
            ))}
          </State>
        )}
      </Box>
    </Box>
  )
}
